import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { appInnerCardStyle, appMutedTextStyle, appSectionCardStyle, renderSurfaceHeader } from "../../../../components/general/uiSurfaceStyles";
import NurseDateField from "../NurseDateField";
import {
    STEP01_ADVANCE_DIRECTIVE_OPTIONS,
    STEP01_DENTIST_PHARMACY_FIELDS,
    STEP01_GENDER_OPTIONS,
    STEP01_INSURANCE_OPTIONS,
    STEP01_MARITAL_STATUS_OPTIONS,
    STEP01_SPECIALIST_FIELDS,
} from "../nurseAssessmentFormConstants";
import { useNurseFormAutoSave } from "../useNurseFormAutoSave";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ContactEntry {
    name: string;
    email: string;
    address: string;
    phone: string;
}

export interface PoaEntry {
    name: string;
    isHealthCarePOA: boolean;
    isFinancialPOA: boolean;
    phone: string;
    email: string;
}

export interface Step01FormValues {
    // Header
    assessmentDate: string;
    acesId: string;
    // Demographics
    residentName: string;
    residentBirthdate: string;
    residentBirthplace: string;
    isResidentGenderMale: boolean;
    isResidentGenderFemale: boolean;
    residentPrimaryLanguage: string;
    residentPrimaryLanguageDetail: string;
    residentEthnicBackground: string;
    residentEthnicBackgroundDetail: string;
    assessmentLocation: string;
    assessmentInformationSource: string;
    residentSpouseName: string;
    isResidentMarried: boolean;
    isResidentDivorced: boolean;
    isResidentWidowed: boolean;
    residentChildrensNames: string;
    // Contacts list
    contacts: ContactEntry[];
    // Power of Attorney list
    powersOfAttorney: PoaEntry[];
    // Insurance
    hasMedicaidInsurance: boolean;
    hasMedicareInsurance: boolean;
    hasHospiceInsurance: boolean;
    isVeteran: boolean;
    residentVeteranBranchOfService: string;
    residentHealthInsuranceCompany: string;
    // Physicians
    primaryPhysicianName: string;
    primaryPhysicianClinic: string;
    primaryPhysicianPhone: string;
    specialist1Name: string;
    specialist1Phone: string;
    specialist2Name: string;
    specialist2Phone: string;
    dentistName: string;
    dentistPhone: string;
    pharmacyName: string;
    pharmacyPhone: string;
    preferredHospitalName: string;
    preferredHospitalLocation: string;
    // Advance Directives
    hasAdvanceDirectivesOnFile: boolean;
    hasPOLSTOnFile: boolean;
    hasFuneralArrangementsOnFile: boolean;
    funeralHomeName: string;
    funeralHomePhone: string;
    residentHeight: string;
    residentWeight: string;
    additionalCommentsInResidentDemographic: string;
}

export const DEFAULT_STEP01: Step01FormValues = {
    assessmentDate: "",
    acesId: "",
    residentName: "",
    residentBirthdate: "",
    residentBirthplace: "",
    isResidentGenderMale: false,
    isResidentGenderFemale: false,
    residentPrimaryLanguage: "",
    residentPrimaryLanguageDetail: "",
    residentEthnicBackground: "",
    residentEthnicBackgroundDetail: "",
    assessmentLocation: "",
    assessmentInformationSource: "",
    residentSpouseName: "",
    isResidentMarried: false,
    isResidentDivorced: false,
    isResidentWidowed: false,
    residentChildrensNames: "",
    contacts: [{ name: "", email: "", address: "", phone: "" }],
    powersOfAttorney: [{ name: "", isHealthCarePOA: false, isFinancialPOA: false, phone: "", email: "" }],
    hasMedicaidInsurance: false,
    hasMedicareInsurance: false,
    hasHospiceInsurance: false,
    isVeteran: false,
    residentVeteranBranchOfService: "",
    residentHealthInsuranceCompany: "",
    primaryPhysicianName: "",
    primaryPhysicianClinic: "",
    primaryPhysicianPhone: "",
    specialist1Name: "",
    specialist1Phone: "",
    specialist2Name: "",
    specialist2Phone: "",
    dentistName: "",
    dentistPhone: "",
    pharmacyName: "",
    pharmacyPhone: "",
    preferredHospitalName: "",
    preferredHospitalLocation: "",
    hasAdvanceDirectivesOnFile: false,
    hasPOLSTOnFile: false,
    hasFuneralArrangementsOnFile: false,
    funeralHomeName: "",
    funeralHomePhone: "",
    residentHeight: "",
    residentWeight: "",
    additionalCommentsInResidentDemographic: "",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const LabeledField: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
    <div className="flex flex-column gap-1 mb-3">
        <label className="font-medium text-sm" style={{ color: "#374151" }}>
            {label}{required && <span style={{ color: "#dc2626", marginLeft: 2 }}>*</span>}
        </label>
        {children}
    </div>
);

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <div className="col-12 md:col-4 mb-2">
        <div className="text-xs font-medium mb-1" style={{ color: "#6b7280" }}>{label}</div>
        <div className="text-sm font-medium" style={{ color: value ? "#111827" : "#9ca3af" }}>{value || "—"}</div>
    </div>
);

const SectionHeading: React.FC<{ title: string; icon?: string }> = ({ title, icon }) => (
    <div className="flex align-items-center gap-2 mb-3 mt-2">
        {icon && <i className={`${icon} text-orange-700`} style={{ fontSize: "1rem" }} />}
        <span className="font-semibold" style={{ color: "#9a3412", fontSize: "0.95rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>{title}</span>
    </div>
);

const normalizeDateInputValue = (value?: string | null): string => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return "";

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

// ── Component ─────────────────────────────────────────────────────────────────

export interface Step01Props {
    sectionData: Partial<Step01FormValues> | null;
    residentId: string;
    formId: string;
    onSave: (data: Step01FormValues) => Promise<void>;
    onNext: () => void;
    loading?: boolean;
}

const Step01ResidentInfo: React.FC<Step01Props> = ({ sectionData, residentId, formId, onSave, onNext, loading }) => {
    const toast = useRef<Toast>(null);
    const [enableEditing, setEnableEditing] = useState<boolean>(false);

    const defaultValues: Step01FormValues = {
        ...DEFAULT_STEP01,
        ...(sectionData || {}),
        assessmentDate: normalizeDateInputValue(sectionData?.assessmentDate),
        acesId: String((sectionData as any)?.acesId || "").trim(),
        residentBirthdate: normalizeDateInputValue(sectionData?.residentBirthdate),
        contacts: sectionData?.contacts?.length ? sectionData.contacts : DEFAULT_STEP01.contacts,
        powersOfAttorney: sectionData?.powersOfAttorney?.length ? sectionData.powersOfAttorney : DEFAULT_STEP01.powersOfAttorney,
    };

    const { control, handleSubmit, watch, reset, formState: { errors } } = useForm<Step01FormValues>({ defaultValues });

    const {
        fields: contactFields,
        append: appendContact,
        remove: removeContact,
    } = useFieldArray({ control, name: "contacts" });

    const {
        fields: poaFields,
        append: appendPoa,
        remove: removePoa,
    } = useFieldArray({ control, name: "powersOfAttorney" });

    // Silent submit used only by auto-save (has its own indicator, no toast)
    const onSubmit = async (data: Step01FormValues) => {
        await onSave(data);
    };

    // Manual save — shows success / failure toast
    const handleSave = async (data: Step01FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Resident information saved successfully.", life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message || "An error occurred while saving.", life: 4000 });
        }
    };

    // Save & continue — only advances to next step if save succeeds
    const handleSaveAndContinue = async (data: Step01FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Resident information saved. Moving to next step.", life: 2000 });
            onNext();
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message || "An error occurred while saving.", life: 4000 });
        }
    };

    useNurseFormAutoSave({
        enabled: enableEditing && !!formId,
        baselineData: defaultValues,
        watch,
        handleSubmit,
        onSubmit,
    });

    const values = watch();

    return (
        <>
            <Toast ref={toast} />

            <form onSubmit={handleSubmit(handleSave)}>
                {/* ── Action Buttons Top ───────────────────────────────────────── */}
                <div className="flex justify-content-center gap-2 mb-3">
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>

                {!enableEditing ? (
                    <>
                        {/* ── READ-ONLY SUMMARY ───────────────────────────────────── */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Assessment Date", undefined, "pi pi-calendar")}
                            <div className="p-3 grid">
                                <InfoRow label="Date of Assessment" value={values.assessmentDate} />
                            </div>
                        </Card>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Resident Demographics", undefined, "pi pi-user")}
                            <div className="p-3 grid">
                                <InfoRow label="ACES ID" value={values.acesId} />
                                <InfoRow label="Full Name" value={values.residentName} />
                                <InfoRow label="Birthdate" value={values.residentBirthdate} />
                                <InfoRow label="Birthplace" value={values.residentBirthplace} />
                                <InfoRow label="Gender" value={values.isResidentGenderMale ? "Male" : values.isResidentGenderFemale ? "Female" : undefined} />
                                <InfoRow
                                    label="Primary Language"
                                    value={values.residentPrimaryLanguageDetail
                                        ? `${values.residentPrimaryLanguage || "Other"} — ${values.residentPrimaryLanguageDetail}`
                                        : values.residentPrimaryLanguage}
                                />
                                <InfoRow
                                    label="Ethnic Background"
                                    value={values.residentEthnicBackgroundDetail
                                        ? `${values.residentEthnicBackground || "Other"} — ${values.residentEthnicBackgroundDetail}`
                                        : values.residentEthnicBackground}
                                />
                                <InfoRow label="Assessment Location" value={values.assessmentLocation} />
                                <InfoRow label="Information Source" value={values.assessmentInformationSource} />
                                <InfoRow label="Marital Status" value={values.isResidentMarried ? "Married" : values.isResidentDivorced ? "Divorced" : values.isResidentWidowed ? "Widowed" : undefined} />
                                <InfoRow label="Spouse Name" value={values.residentSpouseName} />
                                <InfoRow label="Children's Names" value={values.residentChildrensNames} />
                                <InfoRow label="Height" value={values.residentHeight} />
                                <InfoRow label="Weight" value={values.residentWeight} />
                                <InfoRow label="Additional Comments" value={values.additionalCommentsInResidentDemographic} />
                            </div>
                        </Card>
                        {values.contacts?.some(c => c.name) && (
                            <Card style={appSectionCardStyle} className="mb-3">
                                {renderSurfaceHeader("Emergency Contacts", undefined, "pi pi-phone")}
                                <div className="p-3">
                                    {values.contacts.filter(c => c.name).map((c, i) => (
                                        <div key={i} className="grid mb-2">
                                            <InfoRow label={`Contact ${i + 1} \u2014 Name`} value={c.name} />
                                            <InfoRow label="Phone" value={c.phone} />
                                            <InfoRow label="Email" value={c.email} />
                                            <InfoRow label="Address" value={c.address} />
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        )}
                        {values.powersOfAttorney?.some(p => p.name) && (
                            <Card style={appSectionCardStyle} className="mb-3">
                                {renderSurfaceHeader("Powers of Attorney", undefined, "pi pi-briefcase")}
                                <div className="p-3">
                                    {values.powersOfAttorney.filter(p => p.name).map((p, i) => (
                                        <div key={i} className="grid mb-2">
                                            <InfoRow label={`POA ${i + 1} \u2014 Name`} value={p.name} />
                                            <InfoRow label="Phone" value={p.phone} />
                                            <InfoRow label="Email" value={p.email} />
                                            <InfoRow label="Type" value={[p.isHealthCarePOA ? "Healthcare" : "", p.isFinancialPOA ? "Financial" : ""].filter(s => s).join(", ") || undefined} />
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        )}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Insurance & Coverage", undefined, "pi pi-credit-card")}
                            <div className="p-3 grid">
                                <InfoRow label="Insurance" value={[values.hasMedicaidInsurance ? "Medicaid" : "", values.hasMedicareInsurance ? "Medicare" : "", values.hasHospiceInsurance ? "Hospice" : ""].filter(s => s).join(", ") || undefined} />
                                <InfoRow label="Health Insurance Company" value={values.residentHealthInsuranceCompany} />
                                <InfoRow label="Veteran" value={values.isVeteran ? `Yes${values.residentVeteranBranchOfService ? ` \u2014 ${values.residentVeteranBranchOfService}` : ""}` : "No"} />
                            </div>
                        </Card>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Physicians & Pharmacy", undefined, "pi pi-heart-fill")}
                            <div className="p-3 grid">
                                <InfoRow label="Primary Physician" value={values.primaryPhysicianName} />
                                <InfoRow label="Clinic" value={values.primaryPhysicianClinic} />
                                <InfoRow label="Physician Phone" value={values.primaryPhysicianPhone} />
                                <InfoRow label="Specialist 1" value={values.specialist1Name} />
                                <InfoRow label="Specialist 1 Phone" value={values.specialist1Phone} />
                                <InfoRow label="Specialist 2" value={values.specialist2Name} />
                                <InfoRow label="Specialist 2 Phone" value={values.specialist2Phone} />
                                <InfoRow label="Dentist" value={values.dentistName} />
                                <InfoRow label="Dentist Phone" value={values.dentistPhone} />
                                <InfoRow label="Pharmacy" value={values.pharmacyName} />
                                <InfoRow label="Pharmacy Phone" value={values.pharmacyPhone} />
                                <InfoRow label="Preferred Hospital" value={values.preferredHospitalName} />
                                <InfoRow label="Hospital Location" value={values.preferredHospitalLocation} />
                            </div>
                        </Card>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Advance Directives & Other", undefined, "pi pi-file-edit")}
                            <div className="p-3 grid">
                                <InfoRow label="Advance Directives on File" value={values.hasAdvanceDirectivesOnFile ? "Yes" : "No"} />
                                <InfoRow label="POLST on File" value={values.hasPOLSTOnFile ? "Yes" : "No"} />
                                <InfoRow label="Funeral Arrangements on File" value={values.hasFuneralArrangementsOnFile ? "Yes" : "No"} />
                                <InfoRow label="Funeral Home" value={values.funeralHomeName} />
                                <InfoRow label="Funeral Home Phone" value={values.funeralHomePhone} />
                            </div>
                        </Card>
                    </>
                ) : (
                    <>
                        {/* ── Assessment Date ──────────────────────────────────────────── */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Assessment Date", undefined, "pi pi-calendar")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-12 md:col-4">
                                        <LabeledField label="Date of Assessment" required>
                                            <Controller name="assessmentDate" control={control} rules={{ required: "Assessment date is required" }}
                                                render={({ field }) => (
                                                    <NurseDateField
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                        className="w-full"
                                                        placeholder="Date of assessment"
                                                        invalid={!!errors.assessmentDate}
                                                    />
                                                )} />
                                            {errors.assessmentDate && <small className="p-error">{errors.assessmentDate.message}</small>}
                                        </LabeledField>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* ── Demographics ─────────────────────────────────────────────── */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Resident Demographics", undefined, "pi pi-user")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-12 md:col-6">
                                        <LabeledField label="ACES ID">
                                            <Controller name="acesId" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" placeholder="ACES ID" />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <LabeledField label="Individual's Full Name" required>
                                            <Controller name="residentName" control={control} rules={{ required: "Name is required" }}
                                                render={({ field }) => <InputText {...field} className={`w-full ${errors.residentName ? "p-invalid" : ""}`} placeholder="Full name" />} />
                                            {errors.residentName && <small className="p-error">{errors.residentName.message}</small>}
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <LabeledField label="Birthdate">
                                            <Controller name="residentBirthdate" control={control}
                                                render={({ field }) => (
                                                    <NurseDateField
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                        className="w-full"
                                                        placeholder="Birthdate"
                                                    />
                                                )} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <LabeledField label="Birthplace">
                                            <Controller name="residentBirthplace" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" placeholder="City, State, Country" />} />
                                        </LabeledField>
                                    </div>

                                    {/* Gender */}
                                    <div className="col-12 md:col-6">
                                        <LabeledField label="Gender">
                                            <div className="flex gap-4">
                                                {STEP01_GENDER_OPTIONS.map(([fname, lbl]) => (
                                                    <Controller key={fname} name={fname} control={control}
                                                        render={({ field }) => (
                                                            <div className="flex align-items-center gap-2">
                                                                <Checkbox inputId={fname} checked={field.value} onChange={(e) => field.onChange(e.checked)} />
                                                                <label htmlFor={fname} className="cursor-pointer">{lbl}</label>
                                                            </div>
                                                        )} />
                                                ))}
                                            </div>
                                        </LabeledField>
                                    </div>

                                    {/* Marital Status */}
                                    <div className="col-12 md:col-6">
                                        <LabeledField label="Marital Status">
                                            <div className="flex gap-4">
                                                {STEP01_MARITAL_STATUS_OPTIONS.map(([fname, lbl]) => (
                                                    <Controller key={fname} name={fname} control={control}
                                                        render={({ field }) => (
                                                            <div className="flex align-items-center gap-2">
                                                                <Checkbox inputId={fname} checked={field.value} onChange={(e) => field.onChange(e.checked)} />
                                                                <label htmlFor={fname} className="cursor-pointer">{lbl}</label>
                                                            </div>
                                                        )} />
                                                ))}
                                            </div>
                                        </LabeledField>
                                    </div>

                                    <div className="col-12 md:col-4">
                                        <LabeledField label="Primary Language">
                                            <Controller name="residentPrimaryLanguage" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" placeholder="e.g. English" />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <LabeledField label="Language Detail (if other)">
                                            <Controller name="residentPrimaryLanguageDetail" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" placeholder="Specify if needed" />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <LabeledField label="Ethnic Background">
                                            <Controller name="residentEthnicBackground" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <LabeledField label="Ethnic Background Detail">
                                            <Controller name="residentEthnicBackgroundDetail" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <LabeledField label="Assessment Location">
                                            <Controller name="assessmentLocation" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" placeholder="Where was this conducted?" />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <LabeledField label="Information Provided By">
                                            <Controller name="assessmentInformationSource" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" placeholder="Resident, family member, etc." />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <LabeledField label="Spouse's Name">
                                            <Controller name="residentSpouseName" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-8">
                                        <LabeledField label="Children's Names">
                                            <Controller name="residentChildrensNames" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" placeholder="Names separated by commas" />} />
                                        </LabeledField>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* ── Contacts ─────────────────────────────────────────────────── */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Emergency Contacts", undefined, "pi pi-phone")}
                            <div className="p-3">
                                <div style={appMutedTextStyle} className="mb-3">List primary contact first, then alternate contacts.</div>
                                {contactFields.map((field, index) => (
                                    <div key={field.id} style={appInnerCardStyle} className="mb-3 p-3">
                                        <div className="flex justify-content-between align-items-center mb-2">
                                            <SectionHeading title={index === 0 ? "Primary Contact" : `Alternate Contact ${index}`} icon="pi pi-user" />
                                            {contactFields.length > 1 && (
                                                <Button type="button" icon="pi pi-times" severity="danger" outlined rounded size="small"
                                                    onClick={() => removeContact(index)} tooltip="Remove contact" />
                                            )}
                                        </div>
                                        <div className="grid">
                                            <div className="col-12 md:col-4">
                                                <LabeledField label="Name">
                                                    <Controller name={`contacts.${index}.name`} control={control}
                                                        render={({ field: f }) => <InputText {...f} className="w-full" placeholder="Full name" />} />
                                                </LabeledField>
                                            </div>
                                            <div className="col-12 md:col-4">
                                                <LabeledField label="Email">
                                                    <Controller name={`contacts.${index}.email`} control={control}
                                                        render={({ field: f }) => <InputText {...f} className="w-full" type="email" placeholder="email@example.com" />} />
                                                </LabeledField>
                                            </div>
                                            <div className="col-12 md:col-4">
                                                <LabeledField label="Phone">
                                                    <Controller name={`contacts.${index}.phone`} control={control}
                                                        render={({ field: f }) => <InputText {...f} className="w-full" placeholder="(xxx) xxx-xxxx" />} />
                                                </LabeledField>
                                            </div>
                                            <div className="col-12">
                                                <LabeledField label="Address">
                                                    <Controller name={`contacts.${index}.address`} control={control}
                                                        render={({ field: f }) => <InputText {...f} className="w-full" placeholder="Street, City, State, ZIP" />} />
                                                </LabeledField>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <Button type="button" icon="pi pi-plus" label="Add Contact" severity="warning" outlined rounded size="small"
                                    onClick={() => appendContact({ name: "", email: "", address: "", phone: "" })} />
                            </div>
                        </Card>

                        {/* ── Power of Attorney ────────────────────────────────────────── */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Durable Power of Attorney", undefined, "pi pi-briefcase")}
                            <div className="p-3">
                                {poaFields.map((field, index) => (
                                    <div key={field.id} style={appInnerCardStyle} className="mb-3 p-3">
                                        <div className="flex justify-content-between align-items-center mb-2">
                                            <SectionHeading title={`POA #${index + 1}`} icon="pi pi-shield" />
                                            {poaFields.length > 1 && (
                                                <Button type="button" icon="pi pi-times" severity="danger" outlined rounded size="small"
                                                    onClick={() => removePoa(index)} tooltip="Remove POA" />
                                            )}
                                        </div>
                                        <div className="grid">
                                            <div className="col-12 md:col-4">
                                                <LabeledField label="Name">
                                                    <Controller name={`powersOfAttorney.${index}.name`} control={control}
                                                        render={({ field: f }) => <InputText {...f} className="w-full" placeholder="Full name" />} />
                                                </LabeledField>
                                            </div>
                                            <div className="col-12 md:col-4">
                                                <LabeledField label="Phone">
                                                    <Controller name={`powersOfAttorney.${index}.phone`} control={control}
                                                        render={({ field: f }) => <InputText {...f} className="w-full" placeholder="(xxx) xxx-xxxx" />} />
                                                </LabeledField>
                                            </div>
                                            <div className="col-12 md:col-4">
                                                <LabeledField label="Email">
                                                    <Controller name={`powersOfAttorney.${index}.email`} control={control}
                                                        render={({ field: f }) => <InputText {...f} className="w-full" type="email" placeholder="email@example.com" />} />
                                                </LabeledField>
                                            </div>
                                            <div className="col-12">
                                                <LabeledField label="POA Authority">
                                                    <div className="flex gap-4">
                                                        <Controller name={`powersOfAttorney.${index}.isHealthCarePOA`} control={control}
                                                            render={({ field: f }) => (
                                                                <div className="flex align-items-center gap-2">
                                                                    <Checkbox inputId={`poa-hc-${index}`} checked={f.value} onChange={(e) => f.onChange(e.checked)} />
                                                                    <label htmlFor={`poa-hc-${index}`} className="cursor-pointer">Health Care POA</label>
                                                                </div>
                                                            )} />
                                                        <Controller name={`powersOfAttorney.${index}.isFinancialPOA`} control={control}
                                                            render={({ field: f }) => (
                                                                <div className="flex align-items-center gap-2">
                                                                    <Checkbox inputId={`poa-fin-${index}`} checked={f.value} onChange={(e) => f.onChange(e.checked)} />
                                                                    <label htmlFor={`poa-fin-${index}`} className="cursor-pointer">Financial POA</label>
                                                                </div>
                                                            )} />
                                                    </div>
                                                </LabeledField>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <Button type="button" icon="pi pi-plus" label="Add POA" severity="warning" outlined rounded size="small"
                                    onClick={() => appendPoa({ name: "", isHealthCarePOA: false, isFinancialPOA: false, phone: "", email: "" })} />
                            </div>
                        </Card>

                        {/* ── Insurance ────────────────────────────────────────────────── */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Insurance", undefined, "pi pi-credit-card")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-12">
                                        <LabeledField label="Insurance Types">
                                            <div className="flex flex-wrap gap-4">
                                                {STEP01_INSURANCE_OPTIONS.map(({ field: fname, label }) => (
                                                    <Controller key={fname} name={fname} control={control}
                                                        render={({ field: f }) => (
                                                            <div className="flex align-items-center gap-2">
                                                                <Checkbox inputId={`ins-${fname}`} checked={f.value} onChange={(e) => f.onChange(e.checked)} />
                                                                <label htmlFor={`ins-${fname}`} className="cursor-pointer">{label}</label>
                                                            </div>
                                                        )} />
                                                ))}
                                            </div>
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <LabeledField label="Veteran Branch of Service">
                                            <Controller name="residentVeteranBranchOfService" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" placeholder="e.g. Army, Navy, Air Force" />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <LabeledField label="Health Insurance Company">
                                            <Controller name="residentHealthInsuranceCompany" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" />} />
                                        </LabeledField>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* ── Physicians ───────────────────────────────────────────────── */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Physicians & Pharmacy", undefined, "pi pi-heart-fill")}
                            <div className="p-3">
                                <SectionHeading title="Primary Physician" />
                                <div className="grid">
                                    <div className="col-12 md:col-4">
                                        <LabeledField label="Physician Name">
                                            <Controller name="primaryPhysicianName" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <LabeledField label="Clinic / Address">
                                            <Controller name="primaryPhysicianClinic" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <LabeledField label="Phone">
                                            <Controller name="primaryPhysicianPhone" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" />} />
                                        </LabeledField>
                                    </div>
                                </div>
                                <Divider />
                                <SectionHeading title="Specialists" />
                                <div className="grid">
                                    {STEP01_SPECIALIST_FIELDS.map(([nameField, phoneField, label]) => (
                                        <React.Fragment key={nameField}>
                                            <div className="col-12 md:col-6">
                                                <LabeledField label={`${label} Name`}>
                                                    <Controller name={nameField} control={control}
                                                        render={({ field }) => <InputText {...field} className="w-full" />} />
                                                </LabeledField>
                                            </div>
                                            <div className="col-12 md:col-6">
                                                <LabeledField label={`${label} Phone`}>
                                                    <Controller name={phoneField} control={control}
                                                        render={({ field }) => <InputText {...field} className="w-full" />} />
                                                </LabeledField>
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </div>
                                <Divider />
                                <div className="grid">
                                    {STEP01_DENTIST_PHARMACY_FIELDS.map(([nameField, phoneField, label]) => (
                                        <React.Fragment key={nameField}>
                                            <div className="col-12 md:col-6">
                                                <LabeledField label={`${label} Name`}>
                                                    <Controller name={nameField} control={control}
                                                        render={({ field }) => <InputText {...field} className="w-full" />} />
                                                </LabeledField>
                                            </div>
                                            <div className="col-12 md:col-6">
                                                <LabeledField label={`${label} Phone`}>
                                                    <Controller name={phoneField} control={control}
                                                        render={({ field }) => <InputText {...field} className="w-full" />} />
                                                </LabeledField>
                                            </div>
                                        </React.Fragment>
                                    ))}
                                    <div className="col-12 md:col-6">
                                        <LabeledField label="Preferred Hospital Name">
                                            <Controller name="preferredHospitalName" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <LabeledField label="Preferred Hospital Location">
                                            <Controller name="preferredHospitalLocation" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" />} />
                                        </LabeledField>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* ── Advance Directives ───────────────────────────────────────── */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Advance Directives & Other", undefined, "pi pi-file-edit")}
                            <div className="p-3">
                                <div className="grid">
                                    <div className="col-12">
                                        <LabeledField label="Documents on File">
                                            <div className="flex flex-wrap gap-4">
                                                {STEP01_ADVANCE_DIRECTIVE_OPTIONS.map(([fname, lbl]) => (
                                                    <Controller key={fname} name={fname} control={control}
                                                        render={({ field: f }) => (
                                                            <div className="flex align-items-center gap-2">
                                                                <Checkbox inputId={`dir-${fname}`} checked={f.value} onChange={(e) => f.onChange(e.checked)} />
                                                                <label htmlFor={`dir-${fname}`} className="cursor-pointer">{lbl}</label>
                                                            </div>
                                                        )} />
                                                ))}
                                            </div>
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <LabeledField label="Funeral Arrangements With">
                                            <Controller name="funeralHomeName" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" placeholder="Funeral home name" />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <LabeledField label="Funeral Home Phone">
                                            <Controller name="funeralHomePhone" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-3" />
                                    <div className="col-12 md:col-3">
                                        <LabeledField label="Height">
                                            <Controller name="residentHeight" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" placeholder={'e.g. 5\'6"'} />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <LabeledField label="Weight">
                                            <Controller name="residentWeight" control={control}
                                                render={({ field }) => <InputText {...field} className="w-full" placeholder="e.g. 145 lbs" />} />
                                        </LabeledField>
                                    </div>
                                    <div className="col-12">
                                        <LabeledField label="Additional Comments">
                                            <Controller name="additionalCommentsInResidentDemographic" control={control}
                                                render={({ field }) => <InputTextarea {...field} className="w-full" rows={3} autoResize />} />
                                        </LabeledField>
                                    </div>
                                </div>
                            </div>
                        </Card>

                    </>
                )}
                {/* ── Navigation Bottom ────────────────────────────────────────── */}
                <div className="flex justify-content-center gap-2 mt-3">
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
            </form>
        </>
    );
};

export default Step01ResidentInfo;
